import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, Car, ExternalLink, ShieldCheck, Heart, UploadCloud, MessageCircle, Phone, Mail } from 'lucide-react';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedRoute } from '../utils/navigation';

export const Footer: React.FC = () => {
  const { 
    lang, 
    easyWayFleetUrl, 
    easyWayCancunAirportUrl, 
    easyWayContactUrl, 
    easyWayWhatsAppUrl, 
    t 
  } = useLanguage();

  return (
    <footer className="bg-[#052a37] text-white/70 text-xs border-t border-[#0d4e64]">
      {/* Top CTA banner for Easy Way */}
      <div className="bg-[#073B4C] py-10 px-4 sm:px-6 lg:px-8 border-b border-white/10">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
          <div className="space-y-1">
            <span className="text-[11px] uppercase font-black tracking-widest text-[#D6A84B]">
              {t.footer.topBadge}
            </span>
            <h2 className="text-xl sm:text-2xl font-bold text-white font-['Outfit',sans-serif]">
              {t.footer.topTitle}
            </h2>
            <p className="text-[#F7F3EA]/80 text-xs sm:text-sm max-w-xl">
              {t.footer.topSubtitle}
            </p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <a
              href={easyWayCancunAirportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-extrabold text-xs uppercase tracking-wider text-white bg-[#0F766E] hover:bg-[#0d695d] shadow-lg shadow-[#0F766E]/30 transition-all hover:scale-105 active:scale-95"
            >
              <Car className="w-4 h-4 text-white" />
              <span>{t.footer.quoteBtn}</span>
              <ExternalLink className="w-3 h-3 text-white" />
            </a>
          </div>
        </div>
      </div>

      {/* Main Footer Links & Information */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Col 1: Brand */}
          <div className="space-y-3 md:col-span-1">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-transparent flex items-center justify-center overflow-hidden">
                <img 
                  src="/media/dominios/cancunmexico.travel/icono.webp" 
                  alt="Cancun Mexico Travel Logo" 
                  width={32} 
                  height={32} 
                  loading="lazy" 
                  decoding="async" 
                  className="w-8 h-8 object-contain" 
                />
              </div>
              <span className="font-extrabold text-lg text-white font-['Outfit',sans-serif] tracking-tight">
                CancunMexico<span className="text-[#D6A84B]">.travel</span>
              </span>
            </div>
            <p className="text-[11px] leading-relaxed text-[#F7F3EA]/70">
              {t.footer.brandDesc}
            </p>
          </div>

          {/* Col 2: Destinos */}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-white">{t.footer.destinationsTitle}</p>
            <ul className="space-y-1 text-xs">
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Playa Delfines Viewpoint' : lang === 'pt' ? 'Playa Delfines (Mirante)' : 'Playa Delfines (Mirador)'}</Link></li>
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Tortugas & Langosta Beaches' : lang === 'pt' ? 'Playa Tortugas e Langosta' : 'Playa Tortugas & Langosta'}</Link></li>
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Puerto Morelos & Reef' : lang === 'pt' ? 'Puerto Morelos e Recife' : 'Puerto Morelos & Arrecife'}</Link></li>
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Cenotes Route' : lang === 'pt' ? 'Rota dos Cenotes' : 'Ruta de los Cenotes'}</Link></li>
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Playa del Carmen 5th Ave' : lang === 'pt' ? 'Playa del Carmen e 5ª Av' : 'Playa del Carmen & 5ta Av'}</Link></li>
              <li><Link to={lang === 'es' ? '/' : `/${lang}/`} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Tulum & Chichen Ruins' : lang === 'pt' ? 'Ruínas de Tulum e Chichén' : 'Ruinas de Tulum & Chichén'}</Link></li>
            </ul>
          </div>

          {/* Col 3: Experiencias */}
          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wider text-white">{t.footer.experiencesTitle}</p>
            <ul className="space-y-1 text-xs">
              <li><Link to={getLocalizedRoute('consejos-manejo', lang)} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{t.nav.drivingTips}</Link></li>
              <li><Link to={getLocalizedRoute('consejos-viaje', lang)} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{t.nav.travelTips}</Link></li>
              <li><Link to={getLocalizedRoute('rutas', lang)} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{t.nav.roadtrips}</Link></li>
              <li><Link to={getLocalizedRoute('consejos-manejo', lang)} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Savings Calculator' : lang === 'pt' ? 'Calculadora de Economia' : 'Calculadora de Ahorro'}</Link></li>
              <li><Link to={getLocalizedRoute('consejos-manejo', lang)} className="hover:text-[#D6A84B] transition-colors inline-flex items-center py-1 min-h-[30px]">{lang === 'en' ? 'Highway 307 & Traffic' : lang === 'pt' ? 'Rodovia 307 e Trânsito' : 'Carretera 307 & Tránsito'}</Link></li>
            </ul>
          </div>

          {/* Col 4: Recomendación Easy Way */}
          <div className="space-y-2.5">
            <p className="text-xs font-bold uppercase tracking-wider text-white flex items-center gap-1.5">
              <Car className="w-3.5 h-3.5 text-[#2DD4BF]" />
              <span>Easy Way Rent a Car</span>
              <span className="text-[10px] text-[#D6A84B] font-bold bg-[#073B4C] px-1.5 py-0.5 rounded border border-[#D6A84B]/40">
                {lang === 'en' ? 'Recommended' : lang === 'pt' ? 'Recomendado' : 'Recomendado'}
              </span>
            </p>
            <p className="text-xs text-[#F7F3EA]/80 leading-relaxed">
              {t.footer.recommendationDesc}
            </p>
            <ul className="space-y-1 text-xs pt-1">
              <li>
                <a
                  href={easyWayCancunAirportUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[30px]"
                >
                  <span>{lang === 'en' ? 'Cancun Airport Car Rental' : lang === 'pt' ? 'Aluguel Aeroporto de Cancún' : 'Renta de Autos Aeropuerto Cancún'}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                </a>
              </li>
              <li>
                <a
                  href={easyWayFleetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[30px]"
                >
                  <span>{lang === 'en' ? 'Fleet & Car Models' : lang === 'pt' ? 'Frota de Carros e Modelos' : 'Flota de Autos y Modelos'}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                </a>
              </li>
              <li>
                <a
                  href={easyWayContactUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[30px]"
                >
                  <span>{lang === 'en' ? 'Offices & Customer Support' : lang === 'pt' ? 'Escritórios e Atendimento' : 'Sucursales y Atención al Cliente'}</span>
                  <ExternalLink className="w-2.5 h-2.5 opacity-70" />
                </a>
              </li>
            </ul>
            <div className="space-y-1 text-xs pt-2 border-t border-white/10">
              <a 
                href={easyWayWhatsAppUrl} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-1.5 text-[#2DD4BF] hover:text-[#D6A84B] font-semibold py-1 min-h-[30px]"
              >
                <MessageCircle className="w-3.5 h-3.5 fill-current" />
                <span>WhatsApp: {EASY_WAY_CONTACT.whatsapp.display}</span>
              </a>
              <a 
                href={EASY_WAY_CONTACT.phoneMexico.link} 
                className="inline-flex items-center gap-1.5 hover:text-[#D6A84B] transition-colors py-1 min-h-[30px]"
              >
                <Phone className="w-3 h-3 text-[#D6A84B]" />
                <span>México: {EASY_WAY_CONTACT.phoneMexico.display}</span>
              </a>
              <a 
                href={EASY_WAY_CONTACT.phoneUsa.link} 
                className="inline-flex items-center gap-1.5 hover:text-[#D6A84B] transition-colors py-1 min-h-[30px]"
              >
                <Phone className="w-3 h-3 text-[#D6A84B]" />
                <span>USA / CAN: {EASY_WAY_CONTACT.phoneUsa.display}</span>
              </a>
            </div>
          </div>
        </div>

        {/* Official Resources & Government Links Strip */}
        <div className="mt-8 pt-6 border-t border-white/10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-[#F7F3EA]/80">
            <span className="font-bold uppercase tracking-wider text-white text-[11px]">
              {lang === 'en' ? 'Official Resources & Information:' : lang === 'pt' ? 'Recursos Oficiais e Informações:' : 'Recursos Oficiales e Información:'}
            </span>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
              <a
                href="https://lugares.inah.gob.mx/es/zonas-arqueologicas/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[28px]"
              >
                <span>INAH (Ruinas Mayas)</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
              <a
                href="https://www.gob.mx/sectur/angelesverdes"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[28px]"
              >
                <span>Ángeles Verdes (078)</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
              <a
                href="https://www.asur.com.mx/contenido/cancun"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[28px]"
              >
                <span>ASUR (Aeropuerto CUN)</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
              <a
                href="https://www.gob.mx/conanp"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[28px]"
              >
                <span>CONANP (Arrecifes)</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
              <a
                href="https://www.visitax.gob.mx/sitio/"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-[#D6A84B] transition-colors inline-flex items-center gap-1 py-1 min-h-[28px]"
              >
                <span>Visitax Q. Roo</span>
                <ExternalLink className="w-2.5 h-2.5 opacity-60" />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom copyright & legal disclaimer */}
        <div className="mt-6 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-white/50">
          <p>
            {t.footer.copyright}
          </p>
          <p className="flex items-center gap-1">
            {t.footer.craftedFor}
          </p>
        </div>
      </div>
    </footer>
  );
};
